// crewbit-admin/src/components/money/AuthoringExpensesFilterBar.tsx
//
// Period selector + show-archived toggle for /money/expenses. Pure controlled
// component — parent owns ExpenseFilters state via useExpenses hook.
//
// Search input intentionally omitted v1 per spec §Expenses.8 #18 (post-launch).
// Category dropdown lives on the legacy /financials Expenses tab; admin v1
// of /money/expenses keeps the filter bar minimal to favour T2125 grouping.
//
// Spec: docs/references/MONEY_MODULE_SPEC_v1.md §Expenses.7.2.

import { Archive } from 'lucide-react';
import { palette, radii, spacing, transition } from '../../styles/tokens';
import type { ExpenseFilters } from '../../types';

interface AuthoringExpensesFilterBarProps {
  filters: ExpenseFilters;
  onFiltersChange: (next: ExpenseFilters) => void;
}

const RANGES: { value: ExpenseFilters['dateRange']; label: string }[] = [
  { value: 'month', label: 'This month' },
  { value: 'quarter', label: 'This quarter' },
  { value: 'year', label: 'This year' },
  { value: 'all', label: 'All time' },
];

export function AuthoringExpensesFilterBar({
  filters,
  onFiltersChange,
}: AuthoringExpensesFilterBarProps) {
  const includeArchived = filters.includeArchived ?? false;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: spacing.md,
        flexWrap: 'wrap',
        marginBottom: spacing.md,
      }}
    >
      <div
        style={{
          display: 'flex',
          background: palette.g100,
          borderRadius: radii.sm,
          padding: 2,
        }}
      >
        {RANGES.map((r) => {
          const active = filters.dateRange === r.value;
          return (
            <button
              key={r.value}
              type="button"
              onClick={() => onFiltersChange({ ...filters, dateRange: r.value })}
              style={{
                padding: '6px 14px',
                borderRadius: radii.xs,
                border: 'none',
                cursor: 'pointer',
                fontSize: 12.5,
                fontWeight: 600,
                fontFamily: 'inherit',
                background: active ? palette.g800 : 'transparent',
                color: active ? palette.white : palette.g500,
                transition: `background ${transition.fast}, color ${transition.fast}`,
              }}
            >
              {r.label}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        onClick={() => onFiltersChange({ ...filters, includeArchived: !includeArchived })}
        aria-pressed={includeArchived}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 12px',
          borderRadius: radii.sm,
          border: `1px solid ${includeArchived ? palette.accent : palette.g200}`,
          background: includeArchived ? palette.accentWash : palette.white,
          color: includeArchived ? palette.accent : palette.g600,
          fontSize: 12.5,
          fontWeight: 600,
          fontFamily: 'inherit',
          cursor: 'pointer',
          transition: `background ${transition.fast}, color ${transition.fast}`,
        }}
      >
        <Archive size={13} strokeWidth={2} />
        {includeArchived ? 'Showing archived' : 'Show archived'}
      </button>
    </div>
  );
}
